import { useState } from 'react';
import PropTypes from 'prop-types';

import { formatDate, getClassNames, getFirstDayOfCurrentMonth, getLastDayOfCurrentMonth } from '../../../../utils/util';

import DateRangeDropdown from '../../../common/inputs/dropdown/date-range-dropdown/DateRangeDropdown';
import Button from '../../../common/button/Button';
import Table from '../../../common/table/Table';
import DealStatusPieChart from './deal-status-pie-chart/DealStatusPieChart';
import ProductClassificationsPieChart from './product-classifications-pie-chart/ProductClassificationsPieChart';
import TransactionValueLineChart from './transaction-value-line-chart/TransactionValueLineChart';

import './deal-room-statistics.scss';

const DealRoomStatistics = ({ dealRoomStatistics, retrieveDealRoomStatistics, downloadExcel }) => {
  const [fromDate, setFromDate] = useState(getFirstDayOfCurrentMonth());
  const [toDate, setToDate] = useState(getLastDayOfCurrentMonth());
  const [showTable, setShowTable] = useState(false);

  const handleFromDateChange = (date) => {
    setFromDate(date);
    retrieveDealRoomStatistics(date, toDate);
  };

  const handleToDageChange = (date) => {
    setToDate(date);
    retrieveDealRoomStatistics(fromDate, date);
  };

  const renderGraphs = () => {
    return (
      <section className="deal-room-statistics__graphs">
        <section className="deal-room-statistics__graph">
          <section className="deal-room-statistics__graph__title">
            <p>Deal Status</p>
          </section>
          <DealStatusPieChart dealRoomStatistics={dealRoomStatistics} />
        </section>
        <section className="deal-room-statistics__graph">
          <section className="deal-room-statistics__graph__title">
            <p>Product Classifications</p>
          </section>
          <ProductClassificationsPieChart dealRoomStatistics={dealRoomStatistics} />
        </section>
        <section className="deal-room-statistics__graph">
          <section className="deal-room-statistics__graph__title">
            <p>Transaction Value</p>
          </section>
          <section className="deal-room-statistics__graph__transaction-value">
            <div>
              <p>Concluded Deals</p>
              <p>${Number(dealRoomStatistics.concludedDealsValue).toFixed(2)}</p>
            </div>
            <div>
              <p>Ongoing Deals</p>
              <p>${Number(dealRoomStatistics.ongoingDealsValue).toFixed(2)}</p>
            </div>
          </section>
        </section>
        <section className="deal-room-statistics__graph">
          <section className="deal-room-statistics__graph__title">
            <p>Transaction Value</p>
            <p>(over previous 5 months)</p>
          </section>
          <TransactionValueLineChart dealRoomStatistics={dealRoomStatistics} />
        </section>
      </section>
    );
  };

  const renderTable = () => {
    return (
      <section className="deal-room-statistics__table">
        <Table
          data={dealRoomStatistics.dealRoomStatisticsList.map((statistic) => ({
            deal: `#${statistic.deal}`,
            expressOfInterest: formatDate(statistic.expressionOfInterest, true),
            concluded: formatDate(statistic.concluded, true),
            transactionValue: statistic.transactionValue,
            version: statistic.version,
          }))}
          currentPage={0}
          totalPages={0}
          hiddenColumns={['personExternalReference']}
        />
      </section>
    );
  };

  return (
    <section className="deal-room-statistics">
      <section className="deal-room-statistics__header">
        <h1>Deal Room Statistics</h1>
        <DateRangeDropdown
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDageChange}
        />
      </section>

      {dealRoomStatistics && (
        <section className={getClassNames('deal-room-statistics__content', { table: showTable })}>
          {showTable ? renderTable() : renderGraphs()}
          <section className="deal-room-statistics__buttons">
            <Button onClick={() => downloadExcel(fromDate, toDate)} secondary>
              Download .xlsx
            </Button>
            <Button onClick={() => setShowTable(!showTable)}>{showTable ? 'View as Graph' : 'View as Table'}</Button>
          </section>
        </section>
      )}
    </section>
  );
};

DealRoomStatistics.propTypes = {
  dealRoomStatistics: PropTypes.shape({}),
  retrieveDealRoomStatistics: PropTypes.func.isRequired,
  downloadExcel: PropTypes.func.isRequired,
};

DealRoomStatistics.defaultProps = {
  dealRoomStatistics: undefined,
};

export default DealRoomStatistics;
